import React from "react";
import { Bookmark } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { SavedDeploymentConfigKind } from "@/types/savedDeploymentConfig";

const SHARED_INCLUDED = [
  "Deployment name",
  "Selected GPU / processor",
  "Quantity (replicas / instances)",
  "Duration"
];

const CONTAINER_INCLUDED = [
  "Recipe (if selected)",
  "Container image",
  "Private registry credentials (username & password)",
  "Start command & traffic port",
  "Health check settings",
  "Environment variables (including secret values)",
  "GPUs per container & related settings"
];

const VM_INCLUDED = [
  "VM image type",
  "SSH key selection (which keys are checked)",
  "Network services",
  "Confidential compute setting (when available)"
];

const NOT_INCLUDED = [
  "Payment method & currency",
  "GPU search / filter UI state",
  "Ephemeral runtime IDs (cluster, deployment, resource)"
];

type SavedConfigContentsDialogProps = {
  kind: SavedDeploymentConfigKind;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
};

export const SavedConfigContentsDialog: React.FC<SavedConfigContentsDialogProps> = ({
  kind,
  isOpen,
  onOpenChange
}) => {
  const kindLabel = kind === "vm" ? "Virtual Machine" : "Container";
  const kindIncluded = kind === "vm" ? VM_INCLUDED : CONTAINER_INCLUDED;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="w-full max-w-md gap-0 px-0 py-0" showCloseIcon={false}>
        <DialogHeader className="px-8 pt-4">
          <DialogTitle className="title-h4 text-foreground">
            What’s included in a {kindLabel} config
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 px-8 py-4">
          <p className="subtext-small text-muted-foreground">
            Saved configurations are stored in this browser only — they are not saved to your io.net
            account yet (account sync coming soon). Fields marked with a bookmark on the form are
            included when you save.
          </p>

          <p className="subtext-small text-muted-foreground">
            Signing out removes your saved configurations from this browser. Clearing browser data
            or using another browser or device will also remove them.
          </p>

          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1.5 label-small text-foreground">
              <Bookmark className="h-3.5 w-3.5" />
              Included
            </div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {[...SHARED_INCLUDED, ...kindIncluded].map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="flex flex-col gap-2">
            <div className="label-small text-foreground">Not included</div>
            <ul className="list-disc space-y-1 pl-5 text-sm text-muted-foreground">
              {NOT_INCLUDED.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>

        <DialogFooter className="flex flex-row !justify-center gap-2 rounded-b-lg border-t px-4 py-4">
          <Button type="button" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
